import React, { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { useForm } from 'react-hook-form'
import { showBanner } from '../../global-state/banner-slice'
import { newsletterServices, NewsletterGroupFormData } from '../../services/newsletter-service'
import { NewsletterGroupFormPresenter } from './newsletter-group-form-presenter'
import isEqual from 'lodash/isEqual'

const defaultValues: NewsletterGroupFormData = {
  name: '',
  description: '',
  emails: [],
}

function NewsletterGroupFormContainer() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { groupId } = useParams()
  const isEditMode = Boolean(groupId)

  const newsletterGroupRef = useRef<NewsletterGroupFormData | null>(null)

  const form = useForm<NewsletterGroupFormData>({
    defaultValues,
    mode: 'onBlur',
  })

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (!groupId) return
    const fetchNewsletterGroup = async () => {
      try {
        setIsLoading(true)
        const res = await newsletterServices.getGroupById(groupId)
        if (res?.data) {
          newsletterGroupRef.current = res.data
          form.reset({
            name: res.data.name ?? defaultValues.name,
            description: res.data.description ?? defaultValues.description,
            emails: res.data.emails ?? defaultValues.emails,
          })
        }
      } catch (error) {
        console.error(error)
        dispatch(showBanner({ text: 'Failed to fetch newsletter group', variant: 'error-banner' }))
      } finally {
        setIsLoading(false)
      }
    }
    fetchNewsletterGroup()
  }, [groupId])

  const onSubmit = async (data: NewsletterGroupFormData) => {
    if (data.emails.length === 0) {
      form.setError('emails', { type: 'manual', message: 'At least one email is required' })
      return
    }

    setIsSubmitting(true)
    try {
      if (isEditMode && groupId) {
        if (newsletterGroupRef.current) {
          Object.entries(data).forEach(([key, value]) => {
            if (
              value === null ||
              value === undefined ||
              (typeof value === 'string' &&
                (value.trim() === '' ||
                  isEqual(
                    value,
                    newsletterGroupRef.current?.[key as keyof NewsletterGroupFormData],
                  ))) ||
              (Array.isArray(value) &&
                (value.length === 0 ||
                  isEqual(
                    value,
                    newsletterGroupRef.current?.[key as keyof NewsletterGroupFormData],
                  )))
            ) {
              delete data[key as keyof NewsletterGroupFormData]
            }
          })
        }
        if (Object.keys(data).length === 0) {
          dispatch(showBanner({ text: 'No changes to update', variant: 'warning-banner' }))
          return
        }
        const res = await newsletterServices.updateGroupById(groupId, data)

        if (res?.status === 200) {
          dispatch(showBanner({ text: 'Group updated successfully', variant: 'success-banner' }))
          navigate('/newsletter/groups')
        } else {
          dispatch(
            showBanner({
              text: res?.data?.message || 'Something went wrong! Please try again.',
              variant: 'error-banner',
            }),
          )
        }
      } else {
        const res = await newsletterServices.createGroup(data)
        if (res?.status === 200 || res?.status === 201) {
          dispatch(showBanner({ text: 'Group created successfully', variant: 'success-banner' }))
          navigate('/newsletter/groups')
        } else {
          dispatch(
            showBanner({
              text: res?.data?.message || 'Something went wrong! Please try again.',
              variant: 'error-banner',
            }),
          )
        }
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <NewsletterGroupFormPresenter
      form={form}
      isEditMode={isEditMode}
      isSubmitting={isSubmitting}
      isLoading={isLoading}
      onSubmit={onSubmit}
    />
  )
}

export { NewsletterGroupFormContainer }
